import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import apiService from '../apiService';

export default function BalancesTransfersPage() {
  const { drivers, setSelectedDriver } = useAdmin();
  const [overview, setOverview] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payoutPeriod, setPayoutPeriod] = useState('week');
  const [payingAll, setPayingAll] = useState(false);
  const [payingSingle, setPayingSingle] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [overviewRes, payoutsRes] = await Promise.all([
        apiService.get('/balance/admin/overview', { userType: 'admin' }),
        apiService.get(`/balance/admin/payouts?period=${payoutPeriod}`, { userType: 'admin' })
      ]);
      if (overviewRes.success) setOverview(overviewRes);
      if (payoutsRes.success) setPayouts(payoutsRes.payouts);
    } catch (err) {
      setMsg({ type: 'error', text: 'Erreur chargement données' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [payoutPeriod]);

  const handlePayoutAll = async () => {
    if (!window.confirm('Virer TOUS les soldes positifs maintenant ?')) return;
    try {
      setPayingAll(true);
      const res = await apiService.post('/balance/admin/payout-all', {}, { userType: 'admin' });
      if (res.success) {
        setMsg({ type: 'success', text: `${res.paid_count} virements effectués — ${res.total_amount?.toLocaleString('fr-FR')} CDF` });
        loadData();
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Erreur virement global' });
    } finally { setPayingAll(false); }
  };

  const handlePayoutSingle = async (driver_id) => {
    if (!window.confirm('Virer le solde de ce chauffeur maintenant ?')) return;
    try {
      setPayingSingle(driver_id);
      const res = await apiService.post(`/balance/driver/${driver_id}/payout`, {}, { userType: 'admin' });
      if (res.success) {
        setMsg({ type: 'success', text: `Virement de ${res.amount?.toLocaleString('fr-FR')} CDF pour ${res.driver_name}` });
        loadData();
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Erreur virement' });
    } finally { setPayingSingle(''); }
  };

  const handleUnblock = async (driver_id) => {
    if (!window.confirm('Débloquer ce chauffeur ?')) return;
    try {
      await apiService.post(`/balance/driver/${driver_id}/unblock`, {}, { userType: 'admin' });
      setMsg({ type: 'success', text: 'Compte débloqué' });
      loadData();
    } catch (err) {
      setMsg({ type: 'error', text: 'Erreur déblocage' });
    }
  };

  const cardStyle = { background: 'white', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
  const metricCard = (label, value, color) => (
    <div key={label} style={{ ...cardStyle, flex: 1, minWidth: '200px', textAlign: 'center' }}>
      <div style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: '800', color }}>{value}</div>
    </div>
  );

  if (loading) return <div className="dashboard-content"><p>Chargement...</p></div>;

  return (
    <div className="dashboard-content">
      <h1 className="page-title">💸 Soldes & Virements</h1>
      {msg.text && (
        <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '1rem', background: msg.type === 'success' ? '#ecfdf5' : '#fef2f2', color: msg.type === 'success' ? '#059669' : '#dc2626', fontWeight: '600' }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {metricCard('Total à verser ce lundi', `${(overview?.to_pay?.total_amount || 0).toLocaleString('fr-FR')} CDF`, '#059669')}
        {metricCard('Chauffeurs à payer', overview?.to_pay?.drivers_count || 0, '#F54119')}
        {metricCard('Total des dettes', `${(overview?.in_debt?.total_amount || 0).toLocaleString('fr-FR')} CDF`, '#dc2626')}
        {metricCard('Chauffeurs en dette', overview?.in_debt?.drivers_count || 0, '#FFB900')}
      </div>

      {/* Chauffeurs à payer */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>💰 Chauffeurs à payer ce lundi</h3>
          {overview?.to_pay?.drivers?.length > 0 && (
            <button onClick={handlePayoutAll} disabled={payingAll} style={{ padding: '8px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer', opacity: payingAll ? 0.6 : 1 }}>
              {payingAll ? 'Virement en cours...' : 'Tout virer'}
            </button>
          )}
        </div>
        {(!overview?.to_pay?.drivers?.length) ? (
          <p style={{ color: '#9AA0A8', textAlign: 'center', padding: '1rem' }}>Aucun chauffeur à payer</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E9EBEF' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Chauffeur</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Téléphone</th>
                  <th style={{ textAlign: 'right', padding: '10px 8px' }}>Solde à verser</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Dernier virement</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {overview.to_pay.drivers.map(d => (
                  <tr key={d.driver_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ padding: '10px 8px', fontWeight: '600' }}>{d.name}</td>
                    <td style={{ padding: '10px 8px', color: '#5C636E' }}>{d.phone}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{d.current_balance?.toLocaleString('fr-FR')} CDF</td>
                    <td style={{ padding: '10px 8px', color: '#5C636E', fontSize: '0.85rem' }}>{d.last_payout_at ? new Date(d.last_payout_at).toLocaleDateString('fr-FR') : 'Jamais'}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      <button onClick={() => handlePayoutSingle(d.driver_id)} disabled={payingSingle === d.driver_id} style={{ padding: '6px 14px', background: '#F54119', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem', opacity: payingSingle === d.driver_id ? 0.6 : 1 }}>
                        {payingSingle === d.driver_id ? '...' : 'Virer'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Chauffeurs en dette */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>🔴 Chauffeurs en dette</h3>
        {(!overview?.in_debt?.drivers?.length) ? (
          <p style={{ color: '#9AA0A8', textAlign: 'center', padding: '1rem' }}>Aucun chauffeur en dette</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E9EBEF' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Chauffeur</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Téléphone</th>
                  <th style={{ textAlign: 'right', padding: '10px 8px' }}>Montant dû</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px' }}>Depuis (jours)</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px' }}>Statut</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {overview.in_debt.drivers.map(d => (
                  <tr key={d.driver_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ padding: '10px 8px', fontWeight: '600' }}>{d.name}</td>
                    <td style={{ padding: '10px 8px', color: '#5C636E' }}>{d.phone}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#dc2626' }}>{Math.abs(d.current_balance)?.toLocaleString('fr-FR')} CDF</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>{d.days_in_debt}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      {d.account_blocked ? (
                        <span style={{ background: '#fef2f2', color: '#dc2626', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>BLOQUÉ</span>
                      ) : (
                        <span style={{ background: '#fffbeb', color: '#FF9B00', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>EN COURS</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      {d.account_blocked && (
                        <button onClick={() => handleUnblock(d.driver_id)} style={{ padding: '5px 12px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
                          Débloquer
                        </button>
                      )}
                      <button onClick={() => {
                        const found = drivers.find(dr => dr.driver_id === d.driver_id);
                        if (found) setSelectedDriver(found);
                      }} style={{ padding: '5px 12px', background: '#F4F5F7', color: '#414852', border: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer' }}>
                        Voir détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Historique des virements */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📋 Historique des virements</h3>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['week', 'month', 'year'].map(p => (
              <button key={p} onClick={() => setPayoutPeriod(p)} style={{ padding: '6px 14px', background: payoutPeriod === p ? '#F54119' : '#F4F5F7', color: payoutPeriod === p ? 'white' : '#414852', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.8rem' }}>
                {p === 'week' ? 'Cette semaine' : p === 'month' ? 'Ce mois' : 'Cette année'}
              </button>
            ))}
          </div>
        </div>
        {payouts.length === 0 ? (
          <p style={{ color: '#9AA0A8', textAlign: 'center', padding: '1rem' }}>Aucun virement pour cette période</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E9EBEF' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Chauffeur</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px' }}>Téléphone</th>
                  <th style={{ textAlign: 'right', padding: '10px 8px' }}>Montant viré</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px' }}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.transaction_id} style={{ borderBottom: '1px solid #F4F5F7' }}>
                    <td style={{ padding: '10px 8px', fontSize: '0.85rem' }}>{new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '10px 8px', fontWeight: '600' }}>{p.driver_name}</td>
                    <td style={{ padding: '10px 8px', color: '#5C636E' }}>{p.driver_phone}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{p.amount?.toLocaleString('fr-FR')} CDF</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      {p.serdipay_status === 'sent' && <span style={{ color: '#059669', fontWeight: '600' }}>Envoyé ✓</span>}
                      {p.serdipay_status === 'pending' && <span style={{ color: '#FF9B00', fontWeight: '600' }}>En attente</span>}
                      {p.serdipay_status === 'failed' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                          <span style={{ color: '#dc2626', fontWeight: '600' }}>Échoué ✗</span>
                          <button style={{ padding: '4px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #dc2626', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}>Réessayer</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
